import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, Observable, of, shareReplay } from 'rxjs';

import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class GatekeeperService {
    private readonly http = inject(HttpClient);

    private readonly toggleCache = new Map<string, Observable<boolean>>();
    private readonly publicToggleCache = new Map<string, Observable<boolean>>();

    /**
     * Checks if a feature toggle is active for authenticated pages.
     * Results are cached per featureId for the lifetime of the service.
     *
     * @param featureId - The unique identifier of the feature toggle to check
     * @returns Observable<boolean> - True if the toggle is active, false if inactive or on error
     *
     * @remarks
     * This method makes an API call to the Gatekeeper service to determine if a feature
     * is enabled. If the API call fails, it defaults to returning false (feature disabled).
     */
    isToggleActive(featureId: string): Observable<boolean> {
        if (!this.toggleCache.has(featureId)) {
            const request$ = this.http
                .get<boolean>(
                    `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.GATEKEEPER}`,
                    {
                        params: { featureId },
                    },
                )
                .pipe(
                    catchError((error) => {
                        console.error('Error fetching gatekeeper toggle status', error);
                        return of(false);
                    }),
                    shareReplay(1),
                );
            this.toggleCache.set(featureId, request$);
        }
        return this.toggleCache.get(featureId);
    }

    /**
     * Checks if a feature toggle is active for public (unauthenticated) pages.
     * Results are cached per featureId for the lifetime of the service.
     *
     * @param featureId - The unique identifier of the feature toggle to check
     * @returns Observable<boolean> - True if the toggle is active, false if inactive or on error
     *
     * @remarks
     * This method uses a separate public endpoint that doesn't require authentication.
     * If the API call fails, it defaults to returning false (feature disabled).
     */
    isToggleActiveForPublicPages(featureId: string): Observable<boolean> {
        if (!this.publicToggleCache.has(featureId)) {
            const request$ = this.http
                .get<boolean>(
                    `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.GATEKEEPER_PUBLIC}`,
                    {
                        params: { featureId },
                    },
                )
                .pipe(
                    catchError((error) => {
                        console.error(
                            'Error fetching gatekeeper toggle status for public pages',
                            error,
                        );
                        return of(false);
                    }),
                    shareReplay(1),
                );
            this.publicToggleCache.set(featureId, request$);
        }
        return this.publicToggleCache.get(featureId);
    }
}
